/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import type { Key } from 'react';

import { Autocomplete, Chip, ChipGroup, Grid, IconButton, Typography2 } from '@coursera/cds-core';
import { AddIcon } from '@coursera/cds-icons';

const styles = {
  // CDS Grid `spacing` only applies between `Grid item` children, so it leaves
  // the chips flush against the input here. Add explicit breathing room between
  // the selected chips and the input field instead.
  chips: css`
    margin-bottom: var(--cds-spacing-100);
  `,
};

export type ChipItem = {
  key: string;
  label: string;
};

export type OptionItem = {
  key: string;
  /** Displayed text in the dropdown. */
  label: string;
  /**
   * Text used by CDS Autocomplete for type-ahead matching. Defaults to `label`
   * when omitted. Useful when the displayed label differs from the matchable
   * text (e.g. a "Create" option that displays `Add "foo"` but should match on `foo`).
   */
  textValue?: string;
};

export type ChipAndAutocompleteInputLabels = {
  chipsGroup: string;
  inputPlaceholder: string;
  addButton: string;
  notFound: string;
};

export type ChipAndAutocompleteInputProps = {
  chips: ChipItem[];
  onRemoveChip: (key: string) => void;
  options: OptionItem[];
  onSelectOption: (key: string) => void;
  /** Called when the learner presses Enter or clicks the Add button; receives the trimmed input. */
  onAddFromInput: (trimmedInput: string) => void;
  /** Called on every input change so the parent can load matching options. */
  onInputChange?: (input: string) => void;
  loading?: boolean;
  labels: ChipAndAutocompleteInputLabels;
};

const ChipAndAutocompleteInput = ({
  chips,
  onRemoveChip,
  options,
  onSelectOption,
  onAddFromInput,
  onInputChange,
  loading,
  labels,
}: ChipAndAutocompleteInputProps) => {
  const [input, setInput] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);
  const trimmedInput = input.trim();

  // The CDS Autocomplete retains the last-selected option key as internal state.
  // When a chip is removed, that option reappears in the list still visually
  // "selected" until the dropdown is closed and reopened. Remounting (via `key`)
  // on chip removal resets the internal state cleanly.
  const autocompleteResetKey = React.useRef(0);
  const prevChipsLengthRef = React.useRef(chips.length);
  if (chips.length < prevChipsLengthRef.current) autocompleteResetKey.current += 1;
  prevChipsLengthRef.current = chips.length;

  const updateInput = (next: string) => {
    setInput(next);
    onInputChange?.(next);
  };

  const handleSelectionChange = (key: Key | null) => {
    if (key == null) return;
    onSelectOption(String(key));
    updateInput('');
    // Blur so the next click re-triggers focus and reopens the dropdown.
    // (popoverTrigger="focus" only fires on focus events, not on click when already focused.)
    inputRef.current?.blur();
  };

  // Enter adds whatever is typed — but only when the learner hasn't arrow-keyed
  // to a specific option. If an option is highlighted, `aria-activedescendant`
  // is set and Autocomplete's own `onSelectionChange` commits that option, so we
  // must not also add the raw text.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter' || !trimmedInput) return;
    if (event.currentTarget.getAttribute('aria-activedescendant')) return;
    event.preventDefault();
    onAddFromInput(trimmedInput);
    updateInput('');
    inputRef.current?.blur();
  };

  const handleAddClick = () => {
    onAddFromInput(trimmedInput);
    updateInput('');
  };

  return (
    <Grid container direction="column" spacing={8}>
      {chips.length > 0 && (
        <div css={styles.chips}>
          <ChipGroup label={labels.chipsGroup} variant="delete">
            {chips.map((chip) => (
              <Chip key={chip.key} onClick={() => onRemoveChip(chip.key)}>
                {chip.label}
              </Chip>
            ))}
          </ChipGroup>
        </div>
      )}
      <Autocomplete
        key={autocompleteResetKey.current}
        ref={inputRef}
        fullWidth
        aria-label={labels.inputPlaceholder}
        placeholder={labels.inputPlaceholder}
        value={input}
        onInputChange={updateInput}
        onSelectionChange={handleSelectionChange}
        inputProps={{ onKeyDown: handleKeyDown }}
        popoverTrigger="focus"
        loading={loading}
        allowsCustomValue
        renderNotFoundMessage={() => (
          <Typography2 component="div" variant="bodySecondary" color="supportText">
            {labels.notFound}
          </Typography2>
        )}
        items={options}
        inputActionButtons={
          <IconButton
            icon={<AddIcon />}
            aria-label={labels.addButton}
            tooltip={labels.addButton}
            size="small"
            disabled={!trimmedInput}
            onClick={handleAddClick}
          />
        }
      >
        {(option) => (
          <Autocomplete.Option key={option.key} textValue={option.textValue ?? option.label}>
            {option.label}
          </Autocomplete.Option>
        )}
      </Autocomplete>
    </Grid>
  );
};

export default ChipAndAutocompleteInput;
